import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

type Theme = 'dark' | 'light' | 'auto';
type ResolvedTheme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // For guest users, always use auto-detect
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return 'auto';
    }
    // For logged-in users, use saved preference
    const saved = localStorage.getItem('theme');
    return (saved as Theme) || 'auto';
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('dark');

  // Follow the signed-in writer: load their saved choice when someone signs in (including
  // after a sign-in in this tab), and go back to the device's setting when they sign out.
  const { user, isLoading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      setThemeState('auto');
      localStorage.removeItem('theme');
      return;
    }
    let cancelled = false;
    axios
      .get('/users/profile')
      .then((response) => {
        if (cancelled || !response.data.theme) return;
        setThemeState(response.data.theme as Theme);
        localStorage.setItem('theme', response.data.theme);
      })
      .catch((error) => console.error('Failed to load user theme:', error));
    return () => {
      cancelled = true;
    };
  }, [userId, authLoading]);

  useEffect(() => {
    // Resolve the actual theme based on user preference and system settings
    const resolveTheme = () => {
      if (theme === 'auto') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        return prefersDark ? 'dark' : 'light';
      }
      return theme as ResolvedTheme;
    };

    const resolved = resolveTheme();
    setResolvedTheme(resolved);

    // Apply theme to document
    if (resolved === 'light') {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
      document.documentElement.classList.add('dark');
    }

    // Listen for system theme changes when in auto mode
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setResolvedTheme(e.matches ? 'dark' : 'light');
        if (e.matches) {
          document.documentElement.classList.remove('light');
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
          document.documentElement.classList.add('light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    // Only save theme preference if user is logged in
    const token = localStorage.getItem('accessToken');
    if (token) {
      localStorage.setItem('theme', newTheme);
    }
  };

  // Listen for storage events (login/logout)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken') {
        if (!e.newValue) {
          // User logged out - switch to auto-detect
          setThemeState('auto');
          localStorage.removeItem('theme');
        }
        // If user logged in, the profile load effect will handle theme loading
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
